import { ContentType } from "@amzn/g4sservice-client";
import { Text } from "@amzn/storm-ui";
import { useMountEffect, useUnmountEffect } from "@react-hookz/web";
import { useState } from "react";
import { useStore } from "zustand";

import { ExploreWithAIFilters } from "../../components/discovery/filters/ExploreWithAIFilters";
import { PromptTextArea } from "../../components/discovery/insights/exploreWithAI/PromptTextArea";
import { SelectedContentViewer } from "../../components/discovery/insights/exploreWithAI/SelectedContentViewer";
import { XWAI_ENGLISH_CONTENTS_ONLY } from "../../config/constants";
import { defaultContentSubTypeForMarketingContent } from "../../config/filters.config";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { useG4SContentSelectionLoader } from "../../hooks/insightsFromAI/useG4SContentSelectionLoader";
import { exploreWithAiStore } from "../../store/exploreWithAi";
import { discoveryFilterStore } from "../../store/filters";
import { InsightsFromAIFilterField } from "../../types/filters/insightFromAI";
import { IHubContentType } from "../../types/ihubContentType";
import { IHubContentProvider } from "../../types/providers";
import { isLoading } from "../../utils/api.utils";
import { getFilterOptions } from "../../utils/filters.utils";
import type { InsightsFromAIFilters } from "../../utils/insightFromAI.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import { getInsightsExamplePrompts } from "../../utils/prompts.util";
import { logWithTakt } from "../../utils/telemetry.utils";

export const AccountAgnosticExploreWithAI: React.FunctionComponent = () => {
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isFiltersReady, setIsFiltersReady] = useState(false);
    const [setContentType, setContentSubType, setFilterOptions] = useStore(
        discoveryFilterStore,
        (state) => [
            state.setContentType,
            state.setContentSubType,
            state.setFilterOptions,
        ],
    );
    const [
        setStorePrompt,
        setStoreIsExamplePrompt,
        storeContent,
        storeResponseId,
        storeAsyncStatus,
        setContentTypes,
    ] = useStore(exploreWithAiStore, (state) => [
        state.setPrompt,
        state.setIsExamplePrompt,
        state.selectedContent,
        state.responseId,
        state.asyncStatus,
        state.setContentTypes,
    ]);

    const filters: InsightsFromAIFilters = {
        [InsightsFromAIFilterField.CONTENT_TYPES]: {
            [ContentType.MARKETING_CONTENT]: {
                label: "Marketing Content",
                value: ContentType.MARKETING_CONTENT,
            },
            [ContentType.TEMPLATE]: {
                label: "Templates",
                value: ContentType.TEMPLATE,
            },
        },
        [InsightsFromAIFilterField.BRAND]: {},
        [InsightsFromAIFilterField.BROWSE_NODE]: {},
    };

    useMountEffect(() => {
        logWithTakt(TELEMETRY_ID.ACCOUNT_AGNOSTIC_XWAI_VISITED);
        setContentType(IHubContentType.INSIGHTS);
        setContentSubType(IHubContentType.INSIGHTS, {
            provider: IHubContentProvider.INSIGHTS_FROM_AI,
        });
        setFilterOptions(
            InsightsFromAIFilterField.CONTENT_TYPES,
            getFilterOptions(filters[InsightsFromAIFilterField.CONTENT_TYPES]),
        );
        setIsFiltersReady(true);
    });

    useUnmountEffect(async () => {
        setIsFiltersReady(false);
        setContentType(IHubContentType.MARKETING_CONTENT);
        setContentSubType(
            IHubContentType.MARKETING_CONTENT,
            defaultContentSubTypeForMarketingContent,
        );
    });

    const advertiserId = getAdvertiserId();
    const marketplaceId = getMarketplaceId();
    useG4SContentSelectionLoader({
        advertiserId,
        marketplaceId,
        isInitialLoad,
    });

    const setPrompt = (
        value: string,
        isExamplePrompt: boolean,
        _brandId?: string,
        _browseNodeId?: string,
        contentTypes?: ContentType[],
    ) => {
        setIsInitialLoad(false);
        setStoreIsExamplePrompt(isExamplePrompt);
        setStorePrompt(value);
        setContentTypes(contentTypes);
    };

    return (
        <>
            {isFiltersReady && (
                <PromptTextArea
                    isLoading={isLoading(storeAsyncStatus)}
                    onSubmit={setPrompt}
                    placeholder="Describe your advertiser, objective, or desired insight"
                    examplePrompts={getInsightsExamplePrompts({
                        isAccountAgnostic: true,
                    })}
                    submitButtonText="Explore"
                />
            )}
            {/* Remove the text below once we are able to filter by language */}
            <Text withMargin weight="bold">
                {XWAI_ENGLISH_CONTENTS_ONLY}
            </Text>
            <ExploreWithAIFilters setIsInitialLoad={setIsInitialLoad} />
            <SelectedContentViewer
                responseId={storeResponseId}
                asyncStatus={storeAsyncStatus}
                selectedContents={storeContent}
                totalResults={storeContent.length}
            />
        </>
    );
};
